/**
 * OptimierungErrorCard — zwei Fehler-Archetypen (Claude-Design drawer-v2):
 * 'failed' = technisch (Timeout/Rate-Limit → Wiederholen),
 * 'unconfigured' = semantisch (Slot-Mapping fehlt → Kanal konfigurieren).
 * Beide: 0 Credits berechnet (explizit).
 */
import { AlertCircle, AlertTriangle, RotateCw, Settings, Trash2 } from 'lucide-react'

import { DV2 } from './tokens'
import { attrLabel } from './labels'

export type OptimierungErrorKind = 'failed' | 'unconfigured'

export interface OptimierungErrorCardProps {
  attributeCode: string
  kind: OptimierungErrorKind
  detail?: string
  onRetry?: () => void
  onConfigure?: () => void
  onDiscard?: () => void
}

const COPY = {
  failed: {
    title: 'Generierung fehlgeschlagen',
    msg: 'Die KI hat nicht geantwortet. Das passiert manchmal bei hoher Last — der zweite Versuch klappt meistens.',
  },
  unconfigured: {
    title: 'Für diesen Kanal nicht konfiguriert',
    msg: 'Es ist kein Slot-Mapping (Bullet1…Bullet5) hinterlegt — wir können daher nichts schreiben.',
  },
}

export function OptimierungErrorCard({
  attributeCode,
  kind,
  detail,
  onRetry,
  onConfigure,
  onDiscard,
}: OptimierungErrorCardProps) {
  const copy = COPY[kind]
  const Icon = kind === 'failed' ? AlertTriangle : AlertCircle
  return (
    <div className={`rounded-xl border ${DV2.errBd} ${DV2.errBg} overflow-hidden`}>
      <div className="flex items-center gap-2.5 px-3.5 py-2.5">
        <span className={DV2.errFg}>
          <Icon size={14} />
        </span>
        <div className="min-w-0 flex-1">
          <div className={`text-sm-minus font-semibold ${DV2.fg1}`}>{attrLabel(attributeCode)}</div>
          <div className={`text-xs-plus ${DV2.errFg}`}>{copy.title}</div>
        </div>
      </div>
      <div className={`space-y-2.5 border-t ${DV2.errBd} px-3.5 py-3`}>
        <div className={`text-xs ${DV2.fg3}`}>{copy.msg}</div>
        {detail && <div className={`rounded-md ${DV2.card2} px-2.5 py-1.5 font-mono text-2xs ${DV2.fg4}`}>{detail}</div>}
        <div className="flex flex-wrap items-center gap-2">
          {kind === 'failed' && onRetry && (
            <button type="button" onClick={onRetry} className={`flex items-center gap-1.5 rounded-md ${DV2.orangeBg} px-3 py-1.5 text-xs font-semibold text-black`}>
              <RotateCw size={12} />
              Wiederholen
            </button>
          )}
          {kind === 'unconfigured' && onConfigure && (
            <button type="button" onClick={onConfigure} className={`flex items-center gap-1.5 rounded-md border ${DV2.lineStrong} px-3 py-1.5 text-xs font-semibold ${DV2.fg1}`}>
              <Settings size={12} />
              Kanal konfigurieren
            </button>
          )}
          {onDiscard && (
            <button type="button" onClick={onDiscard} className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs ${DV2.fg4} hover:${DV2.fg2}`}>
              <Trash2 size={12} />
              Verwerfen
            </button>
          )}
          <span className={`ml-auto font-mono text-2xs ${DV2.fg4}`}><b className={DV2.fg3}>0 Cr</b> berechnet</span>
        </div>
      </div>
    </div>
  )
}
